import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { X, Download, Smartphone, Share, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Context to share install state across the app
const PWAInstallContext = createContext(null);

export const usePWAInstall = () => {
  const context = useContext(PWAInstallContext);
  if (!context) {
    return { canInstall: false, isInstalled: false, triggerInstall: () => {} };
  }
  return context;
};

export function PWAInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroidDevice = /android/i.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true
      || document.referrer.includes('android-app://');
    
    setIsInstalled(isStandalone);

    // Listen for install prompt (Android/Chrome/Edge)
    const handleBeforeInstall = (e) => {
      console.log('beforeinstallprompt fired');
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      console.log('App installed');
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log('Install prompt outcome:', outcome);
        if (outcome === 'accepted') {
          setIsInstalled(true);
        }
        setDeferredPrompt(null);
        return { success: outcome === 'accepted', method: 'prompt' };
      } catch (error) {
        console.error('Install error:', error);
        return { success: false, error };
      }
    }
    return { success: false, method: 'none' };
  }, [deferredPrompt]);

  const canInstall = !isInstalled && (deferredPrompt !== null || isIOS);

  return (
    <PWAInstallContext.Provider value={{ 
      canInstall, 
      isInstalled, 
      isIOS, 
      isAndroid,
      deferredPrompt,
      triggerInstall 
    }}>
      {children}
    </PWAInstallContext.Provider>
  );
}

export function PWAInstallPrompt() {
  const { canInstall, isInstalled, isIOS, deferredPrompt, triggerInstall } = usePWAInstall();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed
    if (isInstalled || dismissed) return;

    // Check if dismissed recently (stored in localStorage)
    const lastDismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (lastDismissed) {
      const dismissedTime = parseInt(lastDismissed);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        setDismissed(true);
        return;
      }
    }

    // Show prompt after delay if can install
    if (canInstall) {
      let isMounted = true;
      const showPromptAfterDelay = async () => {
        await new Promise(resolve => setTimeout(resolve, 8000)); // 8 seconds delay
        if (isMounted) {
          setShowPrompt(true);
        }
      };
      showPromptAfterDelay();
      return () => { isMounted = false; };
    }
  }, [canInstall, isInstalled, dismissed]);

  const handleInstall = async () => {
    const result = await triggerInstall();
    if (result.success) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isInstalled || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-20 left-4 right-4 z-[100] max-w-md mx-auto"
      >
        <Card className="border-orange-200 bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-xl overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-2xl">✈️</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-base">Instalar ViajeJunto</h3>
                    <p className="text-sm text-orange-100 mt-0.5">
                      Acesse mais rápido direto da tela inicial
                    </p>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="text-white/70 hover:text-white p-1 -mr-1 -mt-1"
                    aria-label="Fechar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {isIOS ? (
                  <div className="mt-3 p-3 bg-white/15 rounded-xl">
                    <p className="text-sm text-white flex items-start gap-2">
                      <Share className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>
                        Toque em <strong>Compartilhar</strong> (ícone ↑) no Safari e depois em <strong>"Adicionar à Tela de Início"</strong>
                      </span>
                    </p>
                  </div>
                ) : deferredPrompt ? (
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDismiss}
                      className="flex-1 text-white border border-white/30 hover:bg-white/20"
                    >
                      Depois
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleInstall}
                      className="flex-1 bg-white text-orange-600 hover:bg-orange-50 font-semibold"
                    >
                      <Download className="w-4 h-4 mr-1.5" />
                      Instalar
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3 p-3 bg-white/15 rounded-xl">
                    <p className="text-sm text-white">
                      <Smartphone className="w-4 h-4 inline mr-1" />
                      Use o menu do navegador → "Adicionar à tela inicial"
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}

// Component for sidebar/menu install button
export function PWAInstallButton({ className = '', variant = 'default' }) {
  const { canInstall, isInstalled, isIOS, triggerInstall } = usePWAInstall();
  const [showIOSModal, setShowIOSModal] = useState(false);

  if (isInstalled) return null;

  const handleClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
    } else {
      await triggerInstall();
    }
  };

  if (variant === 'sidebar') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`flex items-center gap-3 w-full px-4 py-3 text-left text-slate-600 dark:text-slate-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-600 dark:hover:text-orange-400 rounded-lg transition-colors ${className}`}
        >
          <Download className="w-5 h-5" />
          <span className="font-medium">Instalar App</span>
          <span className="ml-auto text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">
            Novo
          </span>
        </button>

        {/* iOS Instructions Modal */}
        <AnimatePresence>
          {showIOSModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
              onClick={() => setShowIOSModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">📱</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Instalar no iPhone/iPad</h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">1</div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Abra no Safari</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">O app só pode ser instalado pelo Safari</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">2</div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Toque em Compartilhar</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Ícone <Share className="w-4 h-4 inline" /> na barra inferior</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">3</div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Adicionar à Tela de Início</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Role para baixo e toque na opção</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setShowIOSModal(false)}
                  className="w-full mt-6 bg-orange-500 hover:bg-orange-600"
                >
                  Entendi
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      className={`gap-2 ${className}`}
    >
      <Download className="w-4 h-4" />
      Instalar App
    </Button>
  );
}

export default PWAInstallPrompt;
